import {makeAutoObservable, observable, reaction} from "mobx";
import {inject, injectable} from "inversify";
import {AxiosError} from "axios";
import {ViewController} from "data/types/structure";
import {Bindings} from "data/constants/bindings";
import {type IRankingsStore, LeaderboardType} from "data/stores/rankings/rankings.store";
import type {IRoundsStore} from "data/stores/rounds/rounds.store";
import type {IRankingsTableObject} from "data/types/entities";
import {IRound} from "data/providers/json/json.provider";
import {DirectionFilter, RankingsFiltersType, RequestState} from "data/enums";
import {IApiResponse} from "data/services/http";
import type {IModalsStore} from "data/stores/modals/modals.store";
import {SyntheticEvent} from "react";

export interface ILeaderboardFilters {
	type: RankingsFiltersType;
	direction: DirectionFilter;
}

export interface ILeaderboardPageController extends ViewController {
	get rankings(): IRankingsTableObject;
	get nextPage(): boolean;
	get isLoading(): boolean;
	get rounds(): IRound[];
	get selectRound(): number | undefined;
	get filters(): ILeaderboardFilters;
	get isPlayoff(): boolean;
	get type(): LeaderboardType;

	loadMoreHandler: () => void;
	roundChange: (roundId: IRound | undefined) => void;
	changeFilters: (type: RankingsFiltersType) => void;
	changeType: (event: SyntheticEvent, newValue: string) => void;
}

@injectable()
export class LeaderboardPageController implements ILeaderboardPageController {
	@observable private _fetchRankingsDisposer?: ReturnType<typeof reaction>;
	@observable private _requestState = RequestState.PENDING;

	get rankings(): IRankingsTableObject {
		return this._rankingsStore.overallTable;
	}

	get nextPage(): boolean {
		return this._rankingsStore.nextPage;
	}

	get isLoading(): boolean {
		return this._requestState === RequestState.PENDING;
	}

	get isPlayoff() {
		return this._roundsStore.isPlayoff;
	}

	get type() {
		return this._rankingsStore.type;
	}

	private get seasonRounds() {
		return this._roundsStore.completedAndActiveSeasonRounds;
	}

	private get playoffRounds() {
		return this._roundsStore.completedAndActivePlayoffRounds;
	}

	get rounds(): IRound[] {
		if (this.type) {
			return this.playoffRounds;
		}

		return this.seasonRounds;
	}

	get filters() {
		return this._rankingsStore.filters;
	}

	get selectRound() {
		return this._rankingsStore.selectRound;
	}

	constructor(
		@inject(Bindings.RankingsStore) private _rankingsStore: IRankingsStore,
		@inject(Bindings.RoundsStore) private _roundsStore: IRoundsStore,
		@inject(Bindings.ModalsStore) private _modalsStore: IModalsStore
	) {
		makeAutoObservable(this);
	}

	private onSuccess = () => {
		this._requestState = RequestState.SUCCESS;
	};

	private onError = (error: AxiosError<IApiResponse>) => {
		this._requestState = RequestState.ERROR;

		this._modalsStore.showAxiosError(error);
	};

	public roundChange = (round: IRound | undefined): void => {
		this._rankingsStore.setContestId(round?.id);
	};

	loadMoreHandler = () => {
		this._requestState = RequestState.PENDING;

		this._rankingsStore.fetchMoreLeaderboards().then(this.onSuccess).catch(this.onError);
	};

	changeFilters = (type: RankingsFiltersType) => {
		this._rankingsStore.changeFilters(type);
	};

	changeType = (_: SyntheticEvent, type: string) => {
		this._rankingsStore.setType(type as LeaderboardType);
	};

	async init() {
		await this._roundsStore
			.fetchRounds()
			.then(() => {
				const type = this.isPlayoff ? LeaderboardType.Playoff : LeaderboardType.Season;
				this._rankingsStore.setType(type);
			})
			.catch((err) => console.error(err));

		this._fetchRankingsDisposer = reaction(
			() => [this.type, this.filters, this.selectRound],
			(Z) => {
				this._requestState = RequestState.PENDING;

				this._rankingsStore
					.fetchOverallLeaderboard()
					.then(this.onSuccess)
					.catch(this.onError);
			},
			{fireImmediately: true}
		);
	}

	dispose(): void {
		this._fetchRankingsDisposer?.();
	}
}
